type OaiqFn = (
  command: 'init' | 'measure',
  eventName: string,
  params?: Record<string, string | number>,
  options?: { event_id?: string; custom_event_name?: string },
) => void

type FbqFn = ((
  command: 'init' | 'track' | 'trackCustom',
  eventName: string,
  params?: Record<string, string | number>,
  options?: { eventID?: string },
) => void) & { queue?: unknown[]; loaded?: boolean }

const BOOK_DEMO_EVENT_ID_KEY = 'statbox_oaiq_book_demo_event_id'

function getOaiq(): OaiqFn | null {
  if (typeof window === 'undefined') return null
  const oaiq = (window as Window & { oaiq?: OaiqFn }).oaiq
  return typeof oaiq === 'function' ? oaiq : null
}

function getFbq(): FbqFn | null {
  if (typeof window === 'undefined') return null
  const fbq = (window as Window & { fbq?: FbqFn }).fbq
  return typeof fbq === 'function' ? fbq : null
}

function logPixelEvent(name: string, eventId: string): void {
  console.log(`[OpenAI Pixel] ${name}`, eventId)
}

function fireMetaLead(eventId: string): void {
  try {
    getFbq()?.('track', 'Lead', { content_name: 'Book a Demo' }, { eventID: eventId })
    console.log('[Meta Pixel] Lead', eventId)
  } catch {
    // Pixel not loaded yet.
  }
}

function fireMetaSchedule(eventId: string): void {
  try {
    getFbq()?.('track', 'Schedule', { content_name: 'Demo booked' }, { eventID: eventId })
    console.log('[Meta Pixel] Schedule', eventId)
  } catch {
    // Pixel not loaded yet.
  }
}

function fireAppointmentScheduled(eventId: string): void {
  try {
    getOaiq()?.(
      'measure',
      'appointment_scheduled',
      { type: 'customer_action' },
      { event_id: eventId },
    )
    logPixelEvent('appointment_scheduled', eventId)
  } catch {
    // Pixel not loaded yet.
  }
}

function fireBookDemoPageViewed(eventId: string): void {
  try {
    getOaiq()?.(
      'measure',
      'page_viewed',
      { type: 'contents' },
      { event_id: eventId },
    )
    logPixelEvent('page_viewed', eventId)
  } catch {
    // Pixel not loaded yet.
  }
}

/** OpenAI + Meta conversion — fire when a user clicks a Book a Demo CTA. */
export function trackBookDemoClick(): void {
  const eventId = `book_demo_${Date.now()}`
  sessionStorage.setItem(BOOK_DEMO_EVENT_ID_KEY, eventId)
  fireAppointmentScheduled(eventId)
  fireMetaLead(eventId)
}

/**
 * Fire on every /book-demo visit (direct URL or after CTA click).
 * Reuses the click event_id when present so OpenAI can deduplicate.
 */
export function trackBookDemoPageView(): void {
  const fromClick = sessionStorage.getItem(BOOK_DEMO_EVENT_ID_KEY)
  sessionStorage.removeItem(BOOK_DEMO_EVENT_ID_KEY)
  const eventId = fromClick || `book_demo_landing_${Date.now()}`
  fireAppointmentScheduled(eventId)
  fireBookDemoPageViewed(`${eventId}_page`)
}

/** Fire when a Calendly demo is successfully booked. */
export function trackDemoBooked(): void {
  const eventId = `book_demo_confirmed_${Date.now()}`
  fireAppointmentScheduled(eventId)
  fireMetaSchedule(eventId)
}
